import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { requestHandler } from 'lib/api';
import { Dispatch } from 'redux';

import {
  CreateExtensionResult,
  CreateFactoringExtensionBody,
  GetAllFactoringExtensionsResponse,
  GetAvailableDocumentsResponse,
  GetFactoringExtensionResponse,
  PreviewDeleteFactoringExtensionResult,
  UpdateFactoringExtensionParams,
} from './types';

export const EXTENSION_KEYS = {
  all: ['factoringExtensions'] as const,
  list: (query: string) => ['factoringExtensions', 'list', query] as const,
  detail: (id: number | string) =>
    ['factoringExtension', 'detail', String(id)] as const,
  previewDelete: (id: number | string) =>
    ['factoringExtension', 'previewDelete', String(id)] as const,
  documentsAvailable: () => ['factoringDocuments', 'available'] as const,
  documentDebt: (id: string) => ['factoringDocuments', 'debt', id] as const,
};

export const useGetAllFactoringExtensions = (
  dispatch: Dispatch,
  query: string,
) =>
  useQuery({
    queryKey: EXTENSION_KEYS.list(query),
    queryFn: async (): Promise<
      GetAllFactoringExtensionsResponse | undefined
    > => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/extension/factoring?${query}`,
        dispatch,
      });
      if (response) return response.data;
    },
    retry: false,
    enabled: true,
  });

export const useGetFactoringExtension = (
  dispatch: Dispatch,
  id: string,
  enabled = true,
) =>
  useQuery({
    queryKey: EXTENSION_KEYS.detail(id ?? ''),
    queryFn: async (): Promise<GetFactoringExtensionResponse | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/extension/factoring/${id}`,
        dispatch,
      });
      if (response) return response.data;
      return undefined;
    },
    retry: false,
    enabled: enabled && id != null,
  });

export const usePreviewDeleteFactoringExtension = (
  dispatch: Dispatch,
  id: string,
  enabled: boolean,
) =>
  useQuery({
    queryKey: EXTENSION_KEYS.previewDelete(id ?? ''),
    queryFn: async (): Promise<
      PreviewDeleteFactoringExtensionResult | undefined
    > => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/extension/factoring/${id}/preview-delete`,
        dispatch,
      });
      if (response) return response.data;
      return undefined;
    },
    retry: false,
    enabled: enabled && id != null && id !== '',
  });

export const useCreateFactoringExtension = (dispatch: Dispatch) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['factoringExtension', 'create'],
    mutationFn: async (
      body: CreateFactoringExtensionBody,
    ): Promise<CreateExtensionResult[]> => {
      const response = await requestHandler({
        method: 'post',
        url: 'internal/extension/factoring',
        data: body,
        dispatch,
      });
      if (response) return response.data;
      return [];
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: EXTENSION_KEYS.all });
    },
  });
};

export const useUpdateFactoringExtension = (
  dispatch: Dispatch,
  id: number | string,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['factoringExtension', 'update', String(id)],
    mutationFn: async (
      data: UpdateFactoringExtensionParams,
    ): Promise<GetAllFactoringExtensionsResponse | undefined> => {
      const response = await requestHandler({
        method: 'patch',
        url: `internal/extension/factoring/${id}`,
        data,
        dispatch,
      });
      if (response) return response.data;
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: EXTENSION_KEYS.detail(id),
      });
    },
  });
};

export const useGetAvailableDocumentsForExtension = (dispatch: Dispatch) =>
  useQuery({
    queryKey: EXTENSION_KEYS.documentsAvailable(),
    queryFn: async (): Promise<GetAvailableDocumentsResponse | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: 'internal/extension/factoring/documents',
        dispatch,
      });
      if (response) return response.data;
    },
    retry: false,
  });

export const getDocumentWithExtensionData = async (
  dispatch: Dispatch,
  id: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/extension/factoring/documents/${id}`,
    dispatch,
  });
  if (response) return response.data;
};

export const fetchAllFactoringExtensions = async (
  dispatch: Dispatch,
  query: string,
): Promise<GetAllFactoringExtensionsResponse | undefined> => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/extension/factoring?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const fetchFactoringExtension = async (
  dispatch: Dispatch,
  id: number | string,
): Promise<GetFactoringExtensionResponse | undefined> => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/extension/factoring/${id}`,
    dispatch,
  });
  if (response) return response.data;
};

export const fetchAvailableDocumentsForExtension = async (
  dispatch: Dispatch,
): Promise<GetAvailableDocumentsResponse | undefined> => {
  const response = await requestHandler({
    method: 'get',
    url: 'internal/extension/factoring/documents',
    dispatch,
  });
  if (response) return response.data;
};

export const getExtensionFactoringMissingApprovals = async (
  dispatch: Dispatch,
  id: number,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/extension/factoring/${id}/approvals`,
    dispatch,
  });
  if (response) return response.data;
};

export const approveExtension = async (
  dispatch: Dispatch,
  id: number,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/extension/factoring/${id}/approve`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const rejectExtension = async (
  dispatch: Dispatch,
  id: number,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/extension/factoring/${id}/reject`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const useDeleteFactoringExtension = (dispatch: Dispatch) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['factoringExtension', 'delete'],
    mutationFn: async (id: number | string) => {
      const response = await requestHandler({
        method: 'delete',
        url: `internal/extension/factoring/${id}`,
        dispatch,
      });
      if (response) return response.data;
      return undefined;
    },
    onSuccess: async (_, id) => {
      await queryClient.refetchQueries({ queryKey: EXTENSION_KEYS.all });
      if (id != null) {
        queryClient.removeQueries({
          queryKey: EXTENSION_KEYS.previewDelete(id),
        });
      }
    },
  });
};

export type {
  AvailableExtensionDocument,
  CreateExtensionPayload,
  CreateExtensionResult,
  CreateFactoringExtensionBody,
  FactoringExtensionDetail,
  FactoringExtensionListItem,
  GetAllFactoringExtensionsParams,
  GetAllFactoringExtensionsResponse,
  GetAvailableDocumentsResponse,
  GetFactoringExtensionPendingAccountCategory,
  GetFactoringExtensionPendingAccountDetail,
  GetFactoringExtensionResponse,
  Pagination,
  PreviewDeleteFactoringExtensionResult,
  UpdateFactoringExtensionParams,
} from './types';
